/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Send a campaign", description = "Attach an audience, set a schedule, review who is reachable, and control pacing with send limits.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Send a campaign",
        description: "Attach an audience, set a schedule, review who is reachable, and control pacing with send limits.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Create a campaign",
          "depth": 0,
          "id": "create-a-campaign"
        }, {
          "value": "Set the schedule",
          "depth": 0,
          "id": "set-the-schedule"
        }, {
          "value": "Quiet hours",
          "depth": 1,
          "id": "quiet-hours"
        }, {
          "value": "Check who will receive it",
          "depth": 0,
          "id": "check-who-will-receive-it"
        }, {
          "value": "Set send limits",
          "depth": 0,
          "id": "set-send-limits"
        }, {
          "value": "Spread a large send over time",
          "depth": 0,
          "id": "spread-a-large-send-over-time"
        }, {
          "value": "Recipients wait, they aren't dropped",
          "depth": 1,
          "id": "recipients-wait-they-arent-dropped"
        }, {
          "value": "How send rate interacts with your other limits",
          "depth": 1,
          "id": "how-send-rate-interacts-with-your-other-limits"
        }, {
          "value": "Why a campaign is still sending",
          "depth": 1,
          "id": "why-a-campaign-is-still-sending"
        }, {
          "value": "Manage a running campaign",
          "depth": 0,
          "id": "manage-a-running-campaign"
        }, {
          "value": "Stop a send",
          "depth": 1,
          "id": "stop-a-send"
        }, {
          "value": "Re-run or retry a send",
          "depth": 1,
          "id": "re-run-or-retry-a-send"
        }, {
          "value": "Change a live campaign",
          "depth": 1,
          "id": "change-a-live-campaign"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      ul: "ul",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["An email built in ", _jsx(_components.a, {
                href: "/native-delivery/emails",
                children: "Build an email"
              }), ", and an audience in Customer Studio"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A campaign sends a message to an audience on a schedule. Attach the audience, set the schedule, review who is reachable, then activate."
        })
      }), "\n", _jsx(_components.h2, {
        id: "create-a-campaign",
        children: "Create a campaign"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Native Delivery → Campaigns"
          }), " and select ", _jsx(_components.strong, {
            children: "Add campaign → Email"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Add content, either ", _jsx(_components.strong, {
            children: "Choose a template"
          }), " or ", _jsx(_components.strong, {
            children: "Start from scratch"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Attach an audience."
        }), "\n", _jsx(_components.li, {
          children: "Set the schedule."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Activate"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If your workspace requires approvals, the button reads ", _jsx(_components.strong, {
          children: "Submit for approval"
        }), " instead, and the campaign sends once an approver signs off."]
      }), "\n", _jsx(_components.h2, {
        id: "set-the-schedule",
        children: "Set the schedule"
      }), "\n", _jsxs(_components.p, {
        children: ["Choose a ", _jsx(_components.strong, {
          children: "Schedule type"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "When to use it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "One-time"
              })
            }), _jsx(_components.td, {
              children: "A single send, such as an announcement or a promotion."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Recurring"
              })
            }), _jsx(_components.td, {
              children: "A repeating send on a defined cadence, such as a weekly newsletter."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "API-triggered"
              })
            }), _jsx(_components.td, {
              children: "Sends triggered by your own systems. No audience or schedule is defined here."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "For one-time and recurring campaigns, pick a time zone behavior:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Same time for everyone"
          }), " sends at one moment, in a fixed time zone."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipient's local time"
          }), " sends at the chosen local hour for each recipient, so a 9 AM send reaches everyone at their own 9 AM."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Recurring campaigns can run indefinitely, stop on a date, or stop after a set number of runs."
      }), "\n", _jsx(_components.h3, {
        id: "quiet-hours",
        children: "Quiet hours"
      }), "\n", _jsxs(_components.p, {
        children: ["Quiet hours keep messages from going out at unwelcome times of day. They're set for your workspace under ", _jsx(_components.strong, {
          children: "Settings"
        }), ", and campaigns respect them by default."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Quiet hours are measured in each recipient's own time zone, so a recipient whose time zone isn't known is held back rather than sent at an unknown hour. They're reported as ", _jsx(_components.strong, {
            children: "Quiet hours (no timezone)"
          }), ". Map a time zone column on your parent model so these recipients are reachable."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Choose ", _jsx(_components.strong, {
          children: "Recipient's local time"
        }), " so quiet hours are measured against each recipient's own clock. ", _jsx(_components.strong, {
          children: "Ignore quiet hours"
        }), " in send limits overrides them when a message is time-critical."]
      }), "\n", _jsx(_components.h2, {
        id: "check-who-will-receive-it",
        children: "Check who will receive it"
      }), "\n", _jsx(_components.p, {
        children: "Before activating, review how your audience narrows down:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audience size"
          }), " is everyone matching the audience definition."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Contactable"
          }), " is those with an email address."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reachable"
          }), " is those you're also permitted to email, after subscription and channel preferences apply."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Reachable is the number that will receive the campaign."
      }), "\n", _jsx(_components.h2, {
        id: "set-send-limits",
        children: "Set send limits"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Send limits"
        }), " control how fast this message sends and whether it can repeat. Each is optional, and the default is listed beside it."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Limit"
            }), _jsx(_components.th, {
              children: "What it does"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cap total messages"
              })
            }), _jsx(_components.td, {
              children: "Stops after a maximum number of sends. Default: no limit."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Limit send rate"
              })
            }), _jsx(_components.td, {
              children: "Spreads delivery over time instead of sending at once. Default: as fast as possible."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Allow repeat sends"
              })
            }), _jsx(_components.td, {
              children: "Lets a recipient receive this message again after a waiting period. Default: once per recipient."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Ignore quiet hours"
              })
            }), _jsx(_components.td, {
              children: "Sends during your channel's quiet hours. Default: delay until sending is allowed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Ignore frequency caps"
              })
            }), _jsx(_components.td, {
              children: "Sends even if a recipient has hit the channel frequency cap. Default: skip those recipients."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Quiet hours and frequency caps are configured for the whole workspace under ", _jsx(_components.strong, {
          children: "Settings"
        }), ", and a message either respects them or overrides them here."]
      }), "\n", _jsx(_components.h2, {
        id: "spread-a-large-send-over-time",
        children: "Spread a large send over time"
      }), "\n", _jsxs(_components.p, {
        children: ["By default a campaign sends as fast as it can. Turn on ", _jsx(_components.strong, {
          children: "Limit send rate"
        }), " to spread delivery instead. That protects your sending reputation on a large send, and keeps every reply from landing on your support team at once."]
      }), "\n", _jsx(_components.p, {
        children: "Choose how to express it:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Mode"
            }), _jsx(_components.th, {
              children: "What you set"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Rate"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Send up to"
              }), " a number of ", _jsx(_components.strong, {
                children: "messages per"
              }), " minute, hour, or day."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Duration"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Spread sends over"
              }), " a number of hours or days, up to 7 days. Hightouch works out the rate for you."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Pick ", _jsx(_components.strong, {
          children: "Rate"
        }), " when a downstream system has a known throughput you shouldn't exceed. Pick ", _jsx(_components.strong, {
          children: "Duration"
        }), " when you care about finishing by a deadline and not about the hourly number."]
      }), "\n", _jsx(_components.p, {
        children: "Both modes show an estimate as you type. Rate mode estimates how long the send will take. Duration mode estimates the messages per minute needed to finish in your window."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Those estimates divide your audience by the rate at the moment you open the settings. They don't account for quiet hours, recipients who get filtered out, or the ramp at the start of a send. Treat them as a guide, not a delivery promise."
        })
      }), "\n", _jsx(_components.p, {
        children: "You can change the send rate while a campaign is running. The new rate applies to upcoming sends, and messages already handed off are unaffected."
      }), "\n", _jsx(_components.h3, {
        id: "recipients-wait-they-arent-dropped",
        children: "Recipients wait, they aren't dropped"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Rate"
        }), " mode, recipients who don't fit in the current window wait for the next one. Nobody is dropped for being slow."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Duration"
        }), " mode is the exception. Because you've named a finish time, recipients still unsent once the window and a short grace period have passed are dropped and counted as ", _jsx(_components.strong, {
          children: "Expired"
        }), " in the campaign runs list. Give yourself a window your audience size can realistically fit."]
      }), "\n", _jsx(_components.h3, {
        id: "how-send-rate-interacts-with-your-other-limits",
        children: "How send rate interacts with your other limits"
      }), "\n", _jsx(_components.p, {
        children: "A recipient passes through these in order, and the outcome depends on which applies:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Quiet hours"
          }), " move a recipient's send to a permitted time. They delay, never drop."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The send rate"
          }), " decides how many of the recipients who are due right now go out."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subscription, bounce, suppression, frequency, and repeat-send rules"
          }), " drop recipients who fail them."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The workspace daily limit"
          }), " for the channel drops anything over the ceiling for the day."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A drop wins over a delay, because a delayed recipient is still waiting while a dropped one is finished. A recipient inside quiet hours is delayed, not lost. One who clears every rule after the workspace has hit its daily limit is dropped with ", _jsx(_components.strong, {
          children: "Channel daily cap"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The workspace daily limit applies to transactional messages too. Every other send limit lets transactional messages through."
        })
      }), "\n", _jsx(_components.h3, {
        id: "why-a-campaign-is-still-sending",
        children: "Why a campaign is still sending"
      }), "\n", _jsx(_components.p, {
        children: "A campaign with a send rate stays in a sending state for as long as that rate requires, which can be hours or days. Several things stretch it:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Quiet hours"
          }), " hold recipients until their window opens, stretching a send past the estimate."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipient's local time"
          }), " staggers a single run across roughly a day as each time zone comes due."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Recipients go out in batches"
          }), " every half minute or so, which makes sends arrive in visible groups."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The overview shows ", _jsx(_components.strong, {
          children: "Sending messages…"
        }), " with a percent complete and an ", _jsx(_components.strong, {
          children: "N of M messages sent"
        }), " count while a run is underway. A run that filtered recipients out finishes below 100%. That's expected, not a stall. For a finished run, open the campaign runs list and compare ", _jsx(_components.strong, {
          children: "Sent"
        }), " against ", _jsx(_components.strong, {
          children: "Scheduled"
        }), ", with ", _jsx(_components.strong, {
          children: "Skipped"
        }), " and ", _jsx(_components.strong, {
          children: "Expired"
        }), " accounting for the difference."]
      }), "\n", _jsx(_components.p, {
        children: "No send waits longer than 7 days. A recipient still unsent after that is dropped. For sends that need to wait longer than that, use a journey instead."
      }), "\n", _jsx(_components.h2, {
        id: "manage-a-running-campaign",
        children: "Manage a running campaign"
      }), "\n", _jsx(_components.p, {
        children: "A campaign moves through these states:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Status"
            }), _jsx(_components.th, {
              children: "Meaning"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Draft"
              })
            }), _jsx(_components.td, {
              children: "Not yet activated."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Scheduled"
              })
            }), _jsx(_components.td, {
              children: "Activated, waiting for its send time."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Active"
              })
            }), _jsx(_components.td, {
              children: "Activated and running on its schedule."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "In progress"
              })
            }), _jsx(_components.td, {
              children: "Currently sending."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Completed"
              })
            }), _jsx(_components.td, {
              children: "Finished."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Error"
              })
            }), _jsx(_components.td, {
              children: "The last run failed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Archived"
              })
            }), _jsx(_components.td, {
              children: "Retired."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "stop-a-send",
        children: "Stop a send"
      }), "\n", _jsx(_components.p, {
        children: "What stops a send depends on its schedule type."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "One-time"
            }), _jsx(_components.th, {
              children: "Recurring"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cancel"
              })
            }), _jsx(_components.td, {
              children: "Stops the send and completes the campaign. You can't undo it."
            }), _jsx(_components.td, {
              children: "Stops the current send and pauses the schedule. You can resume it."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Archive"
              })
            }), _jsx(_components.td, {
              children: "Retires the campaign. You can't reactivate it."
            }), _jsx(_components.td, {
              children: "Retires the campaign. You can't reactivate it."
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Stopping a send doesn't hold the recipients who haven't received it yet. They're dropped, and resuming starts the next scheduled run with a freshly evaluated audience rather than finishing the interrupted one. Messages already handed off still arrive."
        })
      }), "\n", _jsx(_components.h3, {
        id: "re-run-or-retry-a-send",
        children: "Re-run or retry a send"
      }), "\n", _jsx(_components.p, {
        children: "A one-time campaign sends once. You can't re-run it after it completes, and there's no way to retry the recipients a send failed to reach. To send again, create a new campaign."
      }), "\n", _jsx(_components.p, {
        children: "Delivery itself is retried for you. A recipient the system can't reach on the first attempt is retried in the background for up to 7 days."
      }), "\n", _jsxs(_components.p, {
        children: ["If a run fails, the campaign shows ", _jsx(_components.strong, {
          children: "Error"
        }), " with a banner reading ", _jsx(_components.strong, {
          children: "An error occurred during the most recent campaign run"
        }), ". Select ", _jsx(_components.strong, {
          children: "View details"
        }), " for the message. A recurring campaign picks up again on its next scheduled run. A one-time send can't be restarted, so cancel it and build a new one."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["A failed one-time run may have already sent to some recipients. ", _jsx(_components.strong, {
            children: "Error"
          }), " doesn't mean nothing went out, so check ", _jsx(_components.strong, {
            children: "Sent"
          }), " in the campaign runs list before you send again."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "change-a-live-campaign",
        children: "Change a live campaign"
      }), "\n", _jsx(_components.p, {
        children: "You can edit the subject, preheader, and body of a live campaign. Saving stages the edits without touching what's currently sending."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Publishing while a send is underway splits the audience. Recipients not yet processed get the new version, and the rest get the old one, with no way to predict where the boundary falls. Wait for the send to finish unless the change is urgent."
        })
      }), "\n", _jsx(_components.p, {
        children: "Send limits are the exception. Changes to them take effect on the rest of the send without publishing. The audience and schedule are locked once a campaign is activated."
      }), "\n", _jsx(_components.p, {
        children: "This applies to email. An SMS message can't be edited once its campaign is live, so pause or cancel the campaign to change it."
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/subscriptions-and-consent",
            children: "Subscriptions and consent"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/reporting",
            children: "Reporting"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
